import { apiClient, fetcher } from './apiClient';

/**
 * A user as seen from the platform admin surface — spans every org, so unlike
 * `Member` it carries the owning org rather than assuming the active one.
 */
type AdminUser = {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    org: { id: string; name: string; isInternal: boolean } | null;
    roles: string[];
};

/**
 * Watchly staff roles. These apply across every org, unlike the tenant roles
 * (Owner, Admin, Editor, …) which only mean something inside one org.
 *
 * Kept in sync with User::PLATFORM_SUPER_ADMIN / PLATFORM_ACCOUNT_MANAGER and
 * with AdminUserController::PLATFORM_ROLES.
 */
const PLATFORM_ROLES = ['Watchly Super Admin', 'Watchly Account Manager'] as const;

type PlatformRole = (typeof PLATFORM_ROLES)[number];

const isPlatformRole = (role: string): role is PlatformRole =>
    (PLATFORM_ROLES as readonly string[]).includes(role);

const jsonHeaders = (csrfToken: string | null) => ({
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': csrfToken ?? '',
});

/** Super-admin only: every user on the platform. */
const listAdminUsers = async (): Promise<AdminUser[]> =>
    fetcher<AdminUser[]>('api/admin/users');

const deleteAdminUser = async (
    csrfToken: string | null,
    userId: string,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.delete(`api/admin/users/${userId}`, {
        headers: jsonHeaders(csrfToken),
    });
};

/**
 * Grant a platform role. The API refuses this unless the target belongs to the
 * internal org, since a platform role confers authority across every org.
 */
const assignPlatformRole = async (
    csrfToken: string | null,
    userId: string,
    role: PlatformRole,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.post(
        `api/admin/users/${userId}/platform-roles`,
        JSON.stringify({ role }),
        { headers: jsonHeaders(csrfToken) },
    );
};

const removePlatformRole = async (
    csrfToken: string | null,
    userId: string,
    role: PlatformRole,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.delete(
        `api/admin/users/${userId}/platform-roles/${encodeURIComponent(role)}`,
        { headers: jsonHeaders(csrfToken) },
    );
};

export {
    PLATFORM_ROLES,
    assignPlatformRole,
    deleteAdminUser,
    isPlatformRole,
    listAdminUsers,
    removePlatformRole,
};
export type { AdminUser, PlatformRole };
